<template>
    <Markets
        v-bind:loading="loading"
        v-bind:isShowExpandList="isShowExpandList"
        v-bind:isLive="isLive"
    >
        <template #mainMarket>
            <BetBuilderBetBuilderTab
                v-bind:eventId="match.eventId"
                v-bind:isLive="isLive"
            />

            <BetBuilderMainMarket
                v-bind:isLive="isLive"
                v-bind:isOpenMoreMarket="isOpenMoreMarket"
                v-bind:markets="betBuilderMainMarkets"
                v-bind:match="match"
                v-bind:displaySection="displaySection"
                v-on:update:collapseMainCount="updateCollapseMainCount"
            />
        </template>
        <template #moreMarket="{ isCollapse }">
            <BetBuilderMoreMarket
                v-for="(group, index) in groupedBetBuilderMoreMarkets"
                ref="marketCardCollection"
                v-bind:key="group.key"
                v-bind:class="{ __live: isLive }"
                v-bind:marketOrder="index + 1"
                v-bind:isCollapse="isCollapse"
                v-bind:title="group.title"
                v-bind:markets="group.markets"
                v-bind:match="match"
                v-bind:displaySection="displaySection"
                v-bind:score="getGroupScore(group)"
            />
        </template>
    </Markets>
</template>

<script setup lang="ts">
    import { computed, ref, toRefs, watch } from 'vue';
    import BetBuilderBetBuilderTab from '@/components/betBuilder/BetBuilderBetBuilderTab.vue';
    import BetBuilderMainMarket from '@/components/betBuilder/BetBuilderMainMarket.vue';
    import BetBuilderMoreMarket from '@/components/betBuilder/BetBuilderMoreMarket.vue';
    import { getMarketGroupScore } from '@/components/betBuilder/betBuilderOddsDisplay';
    import Markets from '@/components/match/Markets.vue';
    import type { IMatch } from '@/interface/IMatch';
    import type { IBetBuilderMarket, IMarketGroup } from '@/interface/betBuilder';
    import { BetBuilderMarket, BetBuilderStatType, DisplaySection } from '@/interface/enum';
    import { groupMoreMarkets } from '@/libraries/oddsApi/betBuilderMarketRule';
    import { useBetBuilderMatch } from '@/libraries/oddsApi/composables/useBetBuilderMatch';

    interface Props {
        match: IMatch;
        isOpenMoreMarket?: boolean;
        displaySection?: DisplaySection;
    }

    const props = withDefaults(defineProps<Props>(), {
        isOpenMoreMarket: false,
        displaySection: DisplaySection.Unknown,
    });

    const emit = defineEmits(['updateCount']);

    const { match } = toRefs(props);
    const { betBuilderMatch, loading } = useBetBuilderMatch(match.value.eventId);
    const isLive = computed(() => match.value.isLive);

    const mainMarketType = new Set([
        BetBuilderMarket.SetHandicap,
        BetBuilderMarket.SetTotalGoalsOverUnder,
        BetBuilderMarket.Set1x2,
    ]);

    const mainMatchStatType = new Set([
        BetBuilderStatType.FullTime,
        BetBuilderStatType.FirstHalf,
    ]);

    const isMainMarket = (market: IBetBuilderMarket) => mainMarketType.has(market.marketType.id) && mainMatchStatType.has(market.matchStatType.id);

    const betBuilderMainMarkets = computed(() => betBuilderMatch.value?.markets.filter(market => isMainMarket(market)) ?? []);
    const betBuilderMoreMarkets = computed(() => betBuilderMatch.value?.markets.filter(market => !isMainMarket(market)) ?? []);
    const groupedBetBuilderMoreMarkets = computed(() => groupMoreMarkets(betBuilderMoreMarkets.value));
    const getGroupScore = (group: IMarketGroup) => getMarketGroupScore(match.value, group.markets);

    const isShowExpandList = computed(() => props.isOpenMoreMarket && betBuilderMoreMarkets.value.length > 0);
    const collapseMainCount = ref(0);
    const updateCollapseMainCount = (count: number) => {
        collapseMainCount.value = count;
    };

    const moreCount = computed(() => betBuilderMoreMarkets.value.length + collapseMainCount.value);

    watch(moreCount, (newValue: number) => {
        emit('updateCount', newValue);
    });
</script>
